/**
 * 缓存管理工具
 * 用于处理Service Worker缓存和版本控制
 */

// 版本信息接口
interface VersionInfo {
  version: string;
  buildDate: string;
  changelog: string;
}

// 缓存管理类
class CacheManager {
  private static instance: CacheManager;
  private currentVersion: string = '1.0.0';
  private isUpdateAvailable: boolean = false;

  private constructor() {
    this.init();
  }

  public static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  // 初始化缓存管理
  private async init(): Promise<void> {
    // 监听Service Worker消息
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage.bind(this));
    }

    // 检查版本更新
    await this.checkVersionUpdate();
  }

  // 处理Service Worker消息
  private handleServiceWorkerMessage(event: MessageEvent): void {
    const { type, version } = event.data;
    
    switch (type) {
      case 'VERSION_UPDATE':
        console.log(`Service Worker版本更新: ${version}`);
        this.showUpdateNotification();
        break;
    }
  }

  // 检查版本更新
  private async checkVersionUpdate(): Promise<void> {
    try {
      const response = await fetch('/version.json?t=' + Date.now());
      if (response.ok) {
        const versionInfo: VersionInfo = await response.json();
        
        if (versionInfo.version !== this.currentVersion) {
          this.isUpdateAvailable = true;
          this.currentVersion = versionInfo.version;
          this.showUpdateNotification();
        }
      }
    } catch (error) {
      console.warn('无法获取版本信息:', error);
    }
  }

  // 显示更新通知
  private showUpdateNotification(): void {
    // 在实际应用中，这里可以显示一个更新提示
    console.log(`检测到新版本 ${this.currentVersion}，建议刷新页面`);
    
    // 可以在这里添加UI通知逻辑
    // this.showToast('检测到新版本，点击刷新');
  }

  // 强制刷新页面
  public forceRefresh(): void {
    if ('serviceWorker' in navigator) {
      // 通知Service Worker跳过等待
      navigator.serviceWorker.controller?.postMessage({
        type: 'SKIP_WAITING'
      });
    }
    
    // 清除本地存储（可选）
    localStorage.clear();
    sessionStorage.clear();
    
    // 强制刷新页面
    window.location.reload();
  }

  // 清除所有缓存
  public async clearAllCache(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        // 通知Service Worker清除缓存
        navigator.serviceWorker.controller?.postMessage({
          type: 'CLEAR_CACHE'
        });
        
        // 清除浏览器缓存
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }
        
        console.log('缓存已清除');
      } catch (error) {
        console.error('清除缓存失败:', error);
      }
    }
  }

  // 检查是否有更新可用
  public hasUpdate(): boolean {
    return this.isUpdateAvailable;
  }

  // 获取当前版本
  public getCurrentVersion(): string {
    return this.currentVersion;
  }

  // 注册Service Worker
  public async registerServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        console.log('Service Worker注册成功:', registration);
        
        // 监听更新
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                this.isUpdateAvailable = true;
                this.showUpdateNotification();
              }
            });
          }
        });
      } catch (error) {
        console.error('Service Worker注册失败:', error);
      }
    }
  }
}

// 导出单例实例
export const cacheManager = CacheManager.getInstance();